/**
 * Iter 82.2 Phase 6 — "What's New" Adoption Toast.
 *
 * One-time dismissible toast. No tutorial. No wizard. No recurring prompt.
 * Highlights Iter 82.1 capability gains: expanded search, account settings,
 * password self-service.
 *
 * Storage key: `forgedops.whats_new.iter82_1.dismissed` (localStorage)
 * Auto-shows ~3 seconds after login, slides up from bottom-right.
 */
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Sparkles, X, Search, User, Lock } from "lucide-react";

// Iter 112 F-003 — bump storage key so any stale (auto-shown) state for
// existing users is reset; gate by role so field workers (foreman /
// superintendent / inspector) never see a marketing toast over their
// first-session field workflow.
const KEY = "forgedops.whats_new.iter112.dismissed";
const FIELD_ROLES = new Set(["foreman", "superintendent", "inspector"]);

export default function WhatsNewToast() {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);
  const { pathname } = useLocation();
  const onPlatformConsole = pathname.startsWith("/platform");
  const onAuthPage = pathname.startsWith("/login") || pathname.startsWith("/accept-invite") || pathname === "/";
  const isFieldRole = FIELD_ROLES.has(user?.role);

  useEffect(() => {
    if (onPlatformConsole || onAuthPage) return; // do not interrupt /platform or onboarding routes
    if (isFieldRole) return; // never overlay field workflows
    let cancelled = false;
    try {
      if (window.localStorage.getItem(KEY) === "1") return;
    } catch { return; }
    // Don't surface during the brand-new empty-state moment — a fresh user
    // with zero projects shouldn't have a marketing modal as their first
    // impression. Probe lightly before scheduling the appearance.
    api.get("/projects?limit=1").then((r) => {
      if (cancelled) return;
      const hasProjects = Array.isArray(r.data) ? r.data.length > 0 : (r.data?.items?.length > 0);
      if (!hasProjects) return; // skip — let the empty state breathe
      const t = setTimeout(() => { if (!cancelled) setVisible(true); }, 3000);
      return () => clearTimeout(t);
    }).catch(() => { /* unauth or 5xx — never show */ });
    return () => { cancelled = true; };
  }, [onPlatformConsole, onAuthPage, isFieldRole]);

  const dismiss = () => {
    try { window.localStorage.setItem(KEY, "1"); } catch { /* localStorage unavailable */ }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-6 right-6 z-[400] w-80 max-w-[calc(100vw-3rem)]
                 rounded-lg border border-[var(--fop-primary)]/40
                 bg-[var(--fop-surface)] shadow-2xl
                 animate-in slide-in-from-bottom-4 duration-300"
      data-testid="whats-new-toast"
      role="status"
    >
      <div className="px-4 py-3 border-b border-[var(--fop-border-2)] flex items-center gap-2">
        <Sparkles size={14} className="text-[var(--fop-primary)]" />
        <span className="text-[11px] uppercase tracking-wider text-[var(--fop-text)] font-semibold">
          What&apos;s New in ForgedOps
        </span>
        <button
          onClick={dismiss}
          data-testid="whats-new-close"
          className="ml-auto h-8 w-8 -mr-1 flex items-center justify-center rounded text-[var(--fop-text-2)] hover:bg-[var(--fop-surface-2)]"
          aria-label="Dismiss"
        >
          <X size={14} />
        </button>
      </div>
      <ul className="px-4 py-3 space-y-2 text-[12px] text-[var(--fop-text)]">
        <li className="flex items-start gap-2">
          <Search size={12} className="mt-0.5 text-[var(--fop-primary)] shrink-0" />
          <span>
            <span className="text-[var(--fop-text)] font-medium">Expanded ⌘K Search</span>
            <span className="text-[var(--fop-text-2)] block">
              Find markups, shares, exports, and people — not just objects.
            </span>
          </span>
        </li>
        <li className="flex items-start gap-2">
          <User size={12} className="mt-0.5 text-[var(--fop-primary)] shrink-0" />
          <span>
            <span className="text-[var(--fop-text)] font-medium">Account Settings</span>
            <span className="text-[var(--fop-text-2)] block">
              Update your profile & notification preferences. Bottom of the nav rail.
            </span>
          </span>
        </li>
        <li className="flex items-start gap-2">
          <Lock size={12} className="mt-0.5 text-[var(--fop-primary)] shrink-0" />
          <span>
            <span className="text-[var(--fop-text)] font-medium">Self-Service Password</span>
            <span className="text-[var(--fop-text-2)] block">
              Change your password from Account Settings. Forgot? Use the login page.
            </span>
          </span>
        </li>
      </ul>
      <div className="px-4 py-2.5 border-t border-[var(--fop-border-2)] flex justify-end">
        <button
          onClick={dismiss}
          data-testid="whats-new-got-it"
          className="min-h-[36px] px-4 rounded-md bg-[var(--fop-cta-bg)] text-white text-[12px] font-semibold"
        >Got it</button>
      </div>
    </div>
  );
}
